import React, { memo } from "react";
import { DragEndParams } from "react-native-draggable-flatlist";

import AddToWatchlistActionsheet from "@/components/organisms/AddToWatchlistActionsheet";
import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import PlayerDetailCenter from "@/components/organisms/PlayerDetailCenter";
import PlayerDetailLeft from "@/components/organisms/PlayerDetailLeft";
import PlayerDetailErrorState from "@/features/player-detail/components/molecules/PlayerDetailErrorState";
import { Stack } from "@/components/atoms/Stack";

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

import { PLAYER_DETAIL_SIDEBAR } from "@/config/featureFlags";
import {
  PlayerDetailPlayerMetaFragment,
  WatchlistWatchlistMetaFragment,
} from "@/generated/graphql";
import { PlayerCardData } from "@/types/playerCardTypes";

/**
 * No-op function for optional callbacks
 */
const noop = () => {};

/**
 * Root container class for the player detail screen (SE-5391).
 *
 * The screen previously rendered on the default grey app surface, which (a)
 * diverged from the V1.6 design and the Scouting page, and (b) made the grey
 * `bg-surface-muted` "Saved in watchlists" chips blend into the page instead of
 * reading as distinct boxes. Pinning the root to `bg-surface-base` (white in
 * light mode) matches Scouting (`src/features/scouting/screens/Main/MainView.tsx`)
 * and makes the existing watchlist chips read as bounded boxes against the white
 * surface. Theme-aware: `surface-base` resolves to the dark surface in dark mode.
 */
export const PLAYER_DETAIL_ROOT_CLASS = "flex-1 bg-surface-base";

/**
 * Props interface for Player Detail main view component
 */
interface Props {
  player: PlayerDetailPlayerMetaFragment | null | undefined;
  showActionsheet: boolean;
  isSmallScreen: boolean;
  isDark: boolean;
  isGoBack?: boolean;
  onWatchlistActionsheetClosePress: () => void;
  onAddPress: () => void;
  onAddWatchlistPress: () => void;
  loading: boolean;
  /**
   * Loading state of the page-owned PlayerDetailGetPlayer query. Passing this
   * (alongside `player`) makes PlayerDetailCenter reuse the page's player and
   * skip its own duplicate query (SE-5038).
   */
  playerLoading?: boolean;
  isPlayerInAnyWatchlist: boolean;
  /** Whether the player detail query failed (timeout or error) and is not loading */
  hasError?: boolean;
  /** Retries the player detail query when the error state's CTA is pressed */
  onRetry?: () => void;
  /** Whether a retry is currently in flight — drives the error CTA busy state */
  isRetrying?: boolean;
  promptRef?: React.RefObject<CustomPromptRefProps>;
  // Watchlist props for PlayerDetailLeft
  watchlists?: WatchlistWatchlistMetaFragment[];
  watchlistPlayers?: PlayerCardData[];
  selectedWatchlist?: WatchlistWatchlistMetaFragment;
  loadWatchlists?: boolean;
  loadingPlayers?: boolean;
  refreshing?: boolean;
  onLoadMore?: () => void;
  onRefresh?: () => void;
  onDragEnd?: (params: DragEndParams<PlayerCardData>) => void;
  onRefreshWatchlist?: () => void;
}

/**
 * Main view component for player detail screen with responsive layout
 * @remarks
 * Renders different layouts for mobile (single column) vs desktop (two columns)
 * Desktop: left sidebar (watchlists) + center (player details)
 * Notes are accessible on mobile via the Reports & Notes tab
 * @param root0 - Component props
 * @param root0.player - Player data fragment containing bio data and profile information
 * @param root0.loading - Whether watchlist operations are in progress
 * @param root0.playerLoading - Loading state of the page-owned player query (lets the center column skip its own duplicate fetch)
 * @param root0.promptRef - Ref to custom prompt component for confirmation dialogs
 * @param root0.showActionsheet - Whether the add-to-watchlist action sheet is visible
 * @param root0.isPlayerInAnyWatchlist - Whether player exists in any watchlist
 * @param root0.isSmallScreen - Whether the screen size is mobile/small
 * @param root0.isDark - Whether dark theme is active
 * @param root0.isGoBack - Whether to show back navigation button for similar players
 * @param root0.onRetry - Callback fired when the error state's retry CTA is pressed
 * @param root0.hasError - Whether the player detail query failed and the error state should render
 * @param root0.isRetrying - Whether a retry is in progress (busy state on the CTA)
 * @param root0.onAddPress - Callback fired when adding player to roster
 * @param root0.onAddWatchlistPress - Callback fired when adding player to watchlist
 * @param root0.onWatchlistActionsheetClosePress - Callback fired when closing watchlist action sheet
 * @param root0.watchlists - Array of available watchlists for the left column
 * @param root0.watchlistPlayers - Array of players in the currently selected watchlist
 * @param root0.selectedWatchlist - Currently selected watchlist data
 * @param root0.loadWatchlists - Whether watchlists are being loaded
 * @param root0.loadingPlayers - Whether watchlist players are being loaded
 * @param root0.refreshing - Whether watchlist is being refreshed
 * @param root0.onLoadMore - Callback fired when loading more watchlist players
 * @param root0.onRefresh - Callback fired when refreshing watchlist players
 * @param root0.onDragEnd - Callback fired when drag-and-drop reorder completes
 * @param root0.onRefreshWatchlist - Callback fired when refreshing entire watchlist
 */
const MainView = ({
  player,
  loading,
  playerLoading,
  promptRef,
  showActionsheet,
  isPlayerInAnyWatchlist,
  isSmallScreen,
  isDark,
  isGoBack = false,
  hasError = false,
  onRetry,
  isRetrying = false,
  onAddPress,
  onAddWatchlistPress,
  onWatchlistActionsheetClosePress,
  // Watchlist props
  watchlists = [],
  watchlistPlayers = [],
  selectedWatchlist,
  loadWatchlists = false,
  loadingPlayers = false,
  refreshing = false,
  onLoadMore,
  onRefresh,
  onDragEnd,
  onRefreshWatchlist,
}: Props) =>
  // When the player detail query fails (timeout or error) and is no longer
  // loading, show a recoverable error + retry surface instead of a blank
  // screen or a silent navigate-back (SE-5055). Otherwise render the normal
  // master-detail layout — passing the page-owned player/playerLoading into
  // PlayerDetailCenter so it reuses the single GetPlayer owner (SE-5038).
  hasError ? (
    <PlayerDetailErrorState
      title="Something went wrong"
      subtitle="We couldn't load this player. Please try again."
      onRetry={onRetry ?? noop}
      isRetrying={isRetrying}
    />
  ) : (
    // White page surface (SE-5391): matches Scouting and lets the grey watchlist
    // chips read as distinct boxes. Wraps both the mobile and desktop layouts.
    <Stack
      testID={TEST_ID_PLAYER_DETAIL.ROOT}
      className={PLAYER_DETAIL_ROOT_CLASS}
    >
      {isSmallScreen ? (
        <PlayerDetailCenter
          playerId={player?.id}
          player={player}
          playerLoading={playerLoading ?? false}
          onAddPlayer={onAddPress}
          isGoBack={isGoBack}
        />
      ) : (
        // Desktop layout - optional left sidebar + center content
        <Stack className="flex-1">
          <Stack direction="horizontal" className="flex-1">
            {PLAYER_DETAIL_SIDEBAR === "on" && (
              <PlayerDetailLeft
                loading={loadWatchlists || loadingPlayers}
                loadWatchList={loadWatchlists}
                onCallbackOpen={onRefreshWatchlist}
                refreshing={refreshing}
                players={watchlistPlayers}
                watchlists={watchlists}
                onLoadMore={onLoadMore ?? noop}
                onRefresh={onRefresh ?? noop}
                onDragEnd={onDragEnd}
                activeWatchlist={selectedWatchlist}
              />
            )}
            <PlayerDetailCenter
              playerId={player?.id}
              player={player}
              playerLoading={playerLoading ?? false}
              onAddPlayer={onAddPress}
              isGoBack={isGoBack}
            />
          </Stack>
        </Stack>
      )}

      {showActionsheet && player && (
        <AddToWatchlistActionsheet
          item={player}
          showActionsheet={showActionsheet}
          onClose={onWatchlistActionsheetClosePress}
          onAddPress={onAddPress}
          onAddWatchlistPress={onAddWatchlistPress}
          loading={loading}
          isPlayerInAnyWatchlist={isPlayerInAnyWatchlist}
          promptRef={promptRef}
          isDark={isDark}
        />
      )}

      {/* Legacy notes slide-up removed on mobile — notes accessible via Reports & Notes tab */}
    </Stack>
  );

MainView.displayName = "MainView";

export default memo(MainView);
